// useHouseholdSize — user-scoped "how many people we feed" preference
// that drives auto-scale on the Recipes detail panel and cook mode.
//
// Reads once on mount from user.getProfile, caches the result in
// localStorage so the next session boots with the right default
// instantly (no flash of unscaled values). Setter writes to the
// server and updates both the in-memory state and localStorage so a
// later read can fall back to cache if the network is offline.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api } from "./rpc";

const CACHE_KEY = "hub.household.size";

function readCache(): number | null {
  try {
    const v = localStorage.getItem(CACHE_KEY);
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && n >= 1 && n <= 99 ? Math.floor(n) : null;
  } catch { return null; }
}

function writeCache(size: number | null): void {
  try {
    if (size == null) localStorage.removeItem(CACHE_KEY);
    else localStorage.setItem(CACHE_KEY, String(size));
  } catch { /* ignore */ }
}

export interface UseHouseholdSize {
  size: number | null;
  loading: boolean;
  set: (size: number | null) => Promise<void>;
}

export function useHouseholdSize(): UseHouseholdSize {
  // Start with the cached value so a remount doesn't flash 1x while
  // the GET round-trips. The fetch reconciles in the background.
  const [size, setSize] = useState<number | null>(() => readCache());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.userGetProfile()
      .then((p) => {
        if (cancelled) return;
        setSize(p.householdSize);
        writeCache(p.householdSize);
      })
      .catch(() => { /* keep cached value */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const set = useCallback(async (next: number | null) => {
    // Optimistic update — UI responds immediately, server reconciles.
    setSize(next);
    writeCache(next);
    try {
      const confirmed = await api.userSetHouseholdSize(next);
      if (confirmed !== next) {
        setSize(confirmed);
        writeCache(confirmed);
      }
    } catch { /* network blip — keep optimistic value, drainer can retry */ }
  }, []);

  return { size, loading, set };
}
